import { Fragment, type RefObject } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { QuickReplyItem } from './quickReplyItem';
import { QuickReplyRow } from './QuickReplyRow';

const SKELETON_ROWS = ['w-2/5', 'w-1/2', 'w-1/3'];

interface QuickRepliesListProps<T extends QuickReplyItem> {
    listId: string;
    listRef: RefObject<HTMLUListElement>;
    optionId: (index: number) => string;
    allCount: number;
    filtered: T[];
    query: string;
    activeIndex: number;
    isFetching: boolean;
    errorMessage: string | null;
    onRetry: () => void;
    onHover: (index: number) => void;
    onPick: (reply: T) => void;
}

function QuickRepliesList<T extends QuickReplyItem>({
    listId,
    listRef,
    optionId,
    allCount,
    filtered,
    query,
    activeIndex,
    isFetching,
    errorMessage,
    onRetry,
    onHover,
    onPick
}: QuickRepliesListProps<T>) {
    if (allCount === 0 && isFetching) return <QuickRepliesSkeleton />;
    if (allCount === 0 && errorMessage) {
        return <QuickRepliesError message={errorMessage} onRetry={onRetry} />;
    }
    if (allCount === 0) return <QuickRepliesNotice text="No quick replies" />;
    if (filtered.length === 0) {
        return <QuickRepliesNotice text={`No quick replies match "${query.trim()}"`} />;
    }

    // A nested flex column: the popover only caps its height, so h-full would not resolve.
    return (
        <OverlayScrollArea
            className="flex-1 min-h-0 flex flex-col"
            contentClassName="flex-1 min-h-0"
            trackInsetPx={4}
        >
            <ul
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label="Quick replies"
                className="p-1.5 flex flex-col gap-0.5"
            >
                {filtered.map((reply, index) => (
                    <Fragment key={reply.key}>
                        {reply.group && reply.group !== filtered[index - 1]?.group && (
                            <li
                                role="presentation"
                                className="px-3 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                            >
                                {reply.group}
                            </li>
                        )}
                        <QuickReplyRow
                            id={optionId(index)}
                            index={index}
                            reply={reply}
                            active={index === activeIndex}
                            onHover={onHover}
                            onPick={onPick}
                        />
                    </Fragment>
                ))}
            </ul>
        </OverlayScrollArea>
    );
}

function QuickRepliesSkeleton() {
    return (
        <div
            className="p-3 flex flex-col gap-4"
            aria-busy="true"
            aria-label="Loading quick replies"
        >
            {SKELETON_ROWS.map((titleWidth) => (
                <div key={titleWidth} className="flex flex-col gap-1.5">
                    <div className={`h-3.5 bg-gray-200 rounded animate-pulse ${titleWidth}`} />
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-4/5 bg-gray-200 rounded animate-pulse" />
                </div>
            ))}
        </div>
    );
}

function QuickRepliesError({ message, onRetry }: { message: string; onRetry: () => void }) {
    return (
        <div className="p-4 flex flex-col items-center gap-3 text-center" role="alert">
            <p className="text-sm text-red-500">{message}</p>
            <button
                type="button"
                onClick={onRetry}
                className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
            >
                Retry
            </button>
        </div>
    );
}

function QuickRepliesNotice({ text }: { text: string }) {
    return <p className="px-4 py-6 text-sm text-gray-500 text-center break-words">{text}</p>;
}

export { QuickRepliesList };
